/**
 * F010: ACC Checklist Panel
 *
 * Displays the Annual Compliance Certificate checklist grouped by the 8 INFO 135
 * regulatory guide categories. Supports item update (complete/N/A/evidence),
 * progress bar, lodgement countdown, and a "ready for lodgement" banner.
 *
 * Usage: <AccChecklistPanel year={2026} />
 */

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { parseISO, format } from "date-fns";
import { getAuthHeaders } from "@/lib/queryClient";
import {
  CheckCircle2,
  Circle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { AccGenerateModal, type GenerateAccResult } from "@/components/modals/acc-generate-modal";

// ============================================================================
// TYPES
// ============================================================================

type ItemStatus = "not_started" | "complete" | "not_applicable";

interface AccItem {
  id: string;
  complianceYear: number;
  category: string;
  itemCode: string;
  itemDescription: string;
  isCompliant: boolean | null;
  complianceNotes: string | null;
  evidenceReference: string | null;
  reviewedDate: string | null;
}

interface AccYearData {
  complianceYear: number;
  totalItems: number;
  completedItems: number;
  completionPercentage: number;
  items: AccItem[];
}

// ============================================================================
// CATEGORY GROUPING
// ============================================================================

interface CategoryGroup {
  key: string;
  label: string;
  items: AccItem[];
}

// Maps itemCode prefix → display label (aligned with ACC_TEMPLATE in API)
const CATEGORY_GROUPS: Array<{ prefix: string; label: string }> = [
  { prefix: "RG205", label: "RG 205: General Conduct Obligations" },
  { prefix: "RG206", label: "RG 206: Competence & Training" },
  { prefix: "RG207", label: "RG 207: Financial Requirements" },
  { prefix: "RG209", label: "RG 209: Responsible Lending Conduct" },
  { prefix: "RG210", label: "RG 210: Providing Credit Assistance" },
  { prefix: "INFO135", label: "INFO 135: Record-Keeping Obligations" },
  { prefix: "RG271", label: "RG 271: Internal Dispute Resolution" },
  { prefix: "REP-SUP", label: "Representative Supervision" },
];

function groupItemsByCategory(items: AccItem[]): CategoryGroup[] {
  return CATEGORY_GROUPS.map(({ prefix, label }) => ({
    key: prefix,
    label,
    items: items.filter((item) => item.itemCode?.startsWith(prefix) ?? false),
  })).filter((group) => group.items.length > 0);
}

// ============================================================================
// ITEM STATUS HELPERS
// ============================================================================

function getItemStatus(item: AccItem): ItemStatus {
  if (item.isCompliant === true) return "complete";
  if (item.isCompliant === false) return "not_applicable";
  return "not_started";
}

function statusFromSelect(value: string): ItemStatus {
  if (value === "complete") return "complete";
  if (value === "not_applicable") return "not_applicable";
  return "not_started";
}

function itemStatusToIsCompliant(status: ItemStatus): boolean | null {
  if (status === "complete") return true;
  if (status === "not_applicable") return false;
  return null;
}

// ============================================================================
// ITEM STATUS ICON
// ============================================================================

function ItemStatusIcon({ status }: { status: ItemStatus }) {
  if (status === "complete") {
    return <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />;
  }
  if (status === "not_applicable") {
    return <XCircle className="h-4 w-4 text-slate-400 shrink-0" />;
  }
  return <Circle className="h-4 w-4 text-muted-foreground shrink-0" />;
}

// ============================================================================
// ITEM ROW
// ============================================================================

interface ItemRowProps {
  item: AccItem;
  onUpdate: (item: AccItem) => void;
}

function ItemRow({ item, onUpdate }: ItemRowProps) {
  const status = getItemStatus(item);

  return (
    <div
      className={[
        "flex items-start gap-3 rounded-md px-3 py-2 text-sm transition-colors",
        status === "complete" ? "bg-green-50" : "",
        status === "not_applicable" ? "opacity-60" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-testid={`item-row-${item.id}`}
      data-item-status={status}
    >
      {/* Status icon */}
      <div className="mt-0.5">
        <ItemStatusIcon status={status} />
      </div>

      {/* Item description + metadata */}
      <div className="flex-1 min-w-0">
        <p
          className={[
            "leading-snug",
            status === "not_applicable" ? "line-through text-muted-foreground" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {item.itemDescription}
        </p>

        {/* Metadata row */}
        <div className="flex flex-wrap items-center gap-3 mt-1">
          {status === "not_applicable" && (
            <Badge
              variant="outline"
              className="bg-slate-100 text-slate-500 border-slate-300 text-xs px-1.5 py-0"
            >
              N/A
            </Badge>
          )}
          {status === "complete" && item.reviewedDate && (
            <span className="text-xs text-muted-foreground">
              Completed {format(parseISO(item.reviewedDate), "d MMM yyyy")}
            </span>
          )}
          {item.evidenceReference && (
            <span className="text-xs text-primary truncate max-w-[200px]">
              Ref: {item.evidenceReference}
            </span>
          )}
        </div>
      </div>

      {/* Update button */}
      <Button
        variant="ghost"
        size="sm"
        className="shrink-0 text-xs h-7 px-2"
        data-testid={`button-update-item-${item.id}`}
        onClick={() => onUpdate(item)}
      >
        Update
      </Button>
    </div>
  );
}

// ============================================================================
// CATEGORY SECTION (collapsible accordion)
// ============================================================================

interface CategorySectionProps {
  group: CategoryGroup;
  defaultOpen?: boolean;
  onUpdateItem: (item: AccItem) => void;
}

function CategorySection({
  group,
  defaultOpen = false,
  onUpdateItem,
}: CategorySectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  const completedCount = group.items.filter(
    (i) => i.isCompliant === true
  ).length;
  const naCount = group.items.filter((i) => i.isCompliant === false).length;
  const totalCount = group.items.length;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          className="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium hover:bg-muted/60 transition-colors"
          data-testid={`section-${group.key}`}
        >
          <div className="flex items-center gap-2 text-left">
            {open ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
            <span>{group.label}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            {naCount > 0 && (
              <Badge
                variant="outline"
                className="text-xs bg-slate-100 text-slate-500 border-slate-300"
              >
                {naCount} N/A
              </Badge>
            )}
            <Badge
              variant="outline"
              className={
                completedCount === totalCount - naCount
                  ? "text-xs bg-green-100 text-green-700 border-green-300"
                  : "text-xs"
              }
            >
              {completedCount}/{totalCount}
            </Badge>
          </div>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="space-y-0.5 pb-2">
          {group.items.map((item) => (
            <ItemRow key={item.id} item={item} onUpdate={onUpdateItem} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ============================================================================
// UPDATE ITEM DIALOG
// ============================================================================

interface UpdateItemDialogProps {
  item: AccItem | null;
  onClose: () => void;
  onSaved: () => void;
}

function UpdateItemDialog({ item, onClose, onSaved }: UpdateItemDialogProps) {
  const { toast } = useToast();
  const [status, setStatus] = useState<ItemStatus>("not_started");
  const [evidence, setEvidence] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Initialise form when item changes
  const initialStatus = item ? getItemStatus(item) : "not_started";
  const [prevItemId, setPrevItemId] = useState<string | null>(null);

  if (item && item.id !== prevItemId) {
    setPrevItemId(item.id);
    setStatus(initialStatus);
    setEvidence(item.evidenceReference ?? "");
    setNotes(item.complianceNotes ?? "");
  }

  const handleClose = () => {
    setStatus("not_started");
    setEvidence("");
    setNotes("");
    setPrevItemId(null);
    onClose();
  };

  const handleSave = async () => {
    if (!item) return;

    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        isCompliant: itemStatusToIsCompliant(status),
      };

      if (evidence.trim()) {
        payload.evidenceReference = evidence.trim();
      } else {
        payload.evidenceReference = null;
      }

      if (notes.trim()) {
        payload.complianceNotes = notes.trim();
      } else {
        payload.complianceNotes = null;
      }

      if (status === "complete") {
        payload.reviewedDate = format(new Date(), "yyyy-MM-dd");
      }

      const response = await apiRequest(
        "PATCH",
        `/api/compliance/acc/items/${item.id}`,
        payload
      );

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(
          (body as { error?: string }).error ?? `Server error ${response.status}`
        );
      }

      toast({ title: "Checklist item updated" });
      onSaved();
      handleClose();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to update item";
      toast({ title: message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Update Checklist Item</DialogTitle>
        </DialogHeader>

        {item && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground leading-snug">
              {item.itemDescription}
            </p>

            {/* Status */}
            <div className="space-y-1">
              <Label htmlFor="item-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(statusFromSelect(v))}
              >
                <SelectTrigger
                  id="item-status"
                  data-testid="select-item-status"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_started">Not Started</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                  <SelectItem value="not_applicable">Not Applicable</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Evidence Reference */}
            <div className="space-y-1">
              <Label htmlFor="evidence-ref">Evidence Reference</Label>
              <Input
                id="evidence-ref"
                data-testid="input-evidence-ref"
                value={evidence}
                onChange={(e) => setEvidence(e.target.value)}
                placeholder="Document ID, folder path, or URL"
              />
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <Label htmlFor="compliance-notes">Notes</Label>
              <Textarea
                id="compliance-notes"
                data-testid="input-compliance-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes..."
                rows={3}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            data-testid="button-save-item"
            onClick={handleSave}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// PROPS
// ============================================================================

interface AccChecklistPanelProps {
  /** The compliance year to display. Defaults to current year. */
  year?: number;
  /** If provided, allows the panel to switch to a different year. */
  onYearChange?: (year: number) => void;
}

// ============================================================================
// MAIN PANEL
// ============================================================================

export function AccChecklistPanel({
  year: yearProp,
}: AccChecklistPanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(yearProp ?? currentYear);

  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [updatingItem, setUpdatingItem] = useState<AccItem | null>(null);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const {
    data: accData,
    isLoading,
    isError,
    refetch,
  } = useQuery<AccYearData | null>({
    queryKey: ["/api/compliance/acc", year],
    queryFn: async () => {
      // Use raw fetch so we can handle 404 (apiRequest throws on non-2xx)
      const headers = await getAuthHeaders();
      const response = await fetch(`/api/compliance/acc/${year}`, {
        headers,
        credentials: "include",
      });
      if (response.status === 404) return null;
      if (!response.ok) throw new Error("Failed to fetch ACC checklist");
      return response.json() as Promise<AccYearData>;
    },
  });

  // -------------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------------

  const hasChecklist = !!accData && accData.totalItems > 0;

  // Compute lodgement due date (displayed if we can determine it)
  // We don't store annualComplianceDate in the response, so we show it from
  // the earliest item creation date + 45 days as an approximation.
  // For simplicity: show it if we have checklist data.

  const completedItems = accData?.completedItems ?? 0;
  const totalItems = accData?.totalItems ?? 0;
  const completionPct = accData?.completionPercentage ?? 0;

  // Count N/A items (isCompliant === false)
  const naItems = accData?.items.filter((i) => i.isCompliant === false).length ?? 0;
  const applicableItems = totalItems - naItems;
  const applicableCompleted = accData?.items.filter(
    (i) => i.isCompliant === true
  ).length ?? 0;

  // All complete = all applicable items completed
  const allComplete =
    hasChecklist && applicableItems > 0 && applicableCompleted >= applicableItems;

  const groups = hasChecklist ? groupItemsByCategory(accData!.items) : [];

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleGenerated = (result: GenerateAccResult) => {
    setYear(result.complianceYear);
    void queryClient.invalidateQueries({ queryKey: ["/api/compliance/acc"] });
    toast({
      title: `ACC ${result.complianceYear} checklist ready`,
      description: `Lodgement due ${result.lodgementDueDate}`,
    });
  };

  const handleItemSaved = () => {
    void queryClient.invalidateQueries({ queryKey: ["/api/compliance/acc"] });
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  if (isLoading) {
    return (
      <Card data-testid="acc-checklist-panel">
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card data-testid="acc-checklist-panel">
        <CardContent className="py-12 flex flex-col items-center gap-3 text-center text-muted-foreground">
          <AlertTriangle className="h-6 w-6 text-destructive" />
          <p className="text-sm">Failed to load ACC checklist.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // -------------------------------------------------------------------------
  // Empty state: no checklist generated
  // -------------------------------------------------------------------------

  if (!hasChecklist) {
    return (
      <>
        <Card data-testid="acc-checklist-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              Annual Compliance Certificate
            </CardTitle>
          </CardHeader>
          <CardContent className="py-8 flex flex-col items-center gap-4 text-center">
            <p className="text-sm text-muted-foreground">
              No ACC checklist generated for {year}.
            </p>
            <Button
              data-testid="button-generate-acc"
              onClick={() => setGenerateModalOpen(true)}
            >
              Generate ACC Checklist
            </Button>
          </CardContent>
        </Card>

        <AccGenerateModal
          open={generateModalOpen}
          onClose={() => setGenerateModalOpen(false)}
          onGenerated={handleGenerated}
        />
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Checklist active state
  // -------------------------------------------------------------------------

  return (
    <>
      <div className="space-y-4" data-testid="acc-checklist-panel">
        {/* Header card with progress */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                ACC {year} — Annual Compliance Certificate
              </CardTitle>
            </div>
          </CardHeader>

          <CardContent className="space-y-3 pt-0">
            {/* Progress bar */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span
                  className="text-muted-foreground"
                  data-testid="acc-items-count"
                >
                  {completedItems}/{totalItems} items complete
                  {naItems > 0 && ` (${naItems} N/A)`}
                </span>
                <span className="font-medium tabular-nums">
                  {completionPct}%
                </span>
              </div>
              <Progress
                value={completionPct}
                className="h-2"
                data-testid="acc-progress-bar"
              />
            </div>

            {/* All complete banner */}
            {allComplete && (
              <div
                className="rounded-md bg-green-50 border border-green-200 px-4 py-3 flex items-center gap-3"
                data-testid="acc-complete-banner"
              >
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    ACC checklist complete — ready for lodgement
                  </p>
                  <p className="text-xs text-green-700 mt-0.5">
                    All applicable items have been verified.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category sections */}
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {groups.map((group, idx) => (
                <CategorySection
                  key={group.key}
                  group={group}
                  defaultOpen={idx === 0}
                  onUpdateItem={setUpdatingItem}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Update item dialog */}
      <UpdateItemDialog
        item={updatingItem}
        onClose={() => setUpdatingItem(null)}
        onSaved={handleItemSaved}
      />

      {/* Generate modal (accessible if user wants a different year) */}
      <AccGenerateModal
        open={generateModalOpen}
        onClose={() => setGenerateModalOpen(false)}
        onGenerated={handleGenerated}
      />
    </>
  );
}
